import _ from "underscore";
import { Dialog } from "../../../framework/dialog";
import { show } from "../../../../utils/vanilla";
import Translator from "../../../../utils/translator";
import { CancelablePromise as Promise } from "../../../../utils/cancelablePromise";
import { DriveStore } from "../../../../store/reducers";
import firstDialogHTML from "./exemption.firstOffDutyDeferDialog.tmpl";
import secondDialogHTML from "./exemption.secondOffDutyDeferDialog.tmpl";
import { MINUTES, timeSpanToMs } from "hos-tools";
import { handleApi } from "../../../../utils/error";
import AutoCompletionInput from "../../../framework/autoCompletionInput";

const title = "Off-duty deferral";

export type TSituationActionType = "remove" | "applyADC" | "applyERR" | "applyPC" | "applyExemption" | "startEmergencyResponse" | "endEmergencyResponse" | "apply";

export function createFirstDayDialog(store: DriveStore, submitCallback, translator) {
    let restBreakNeededMin = Math.floor(timeSpanToMs(this.ruleResult.restBreakNeeded) / MINUTES);
    let template = _.template(firstDialogHTML);

    let firstDialog = new Dialog(
        "first-day-dialog",
        template({ restBreakNeededInMinutes: restBreakNeededMin }),
        translator,
        title,
        [
            {
                text: "Cancel",
                callback: () => {
                    firstDialog.remove();
                }
            },
            {
                text: "Submit",
                spinner: true,
                callback: () => {
                    let hoursSelect = <HTMLSelectElement>document.getElementById("durationSelect_Hours");
                    let minutesSelect = <HTMLSelectElement>document.getElementById("durationSelect_Minutes");
                    let hours = hoursSelect.selectedIndex;
                    let mins = minutesSelect.selectedIndex;
                    let duration = hours * 60 + mins;
                    let warning: HTMLElement | null = null;
                    let longDurationWarning: HTMLElement | null = null;

                    if (duration > restBreakNeededMin) {
                        warning = <HTMLElement>document.getElementById("long-duration-warning");
                        longDurationWarning = <HTMLElement>warning.children[0].children[0];
                    } else if (duration === 0) {
                        warning = <HTMLElement>document.getElementById("no-duration-warning");
                    }

                    if (warning) {
                        firstDialog.waiting.stop();
                        if (longDurationWarning) {
                            longDurationWarning.innerText = longDurationWarning.innerText.replace("{0}", restBreakNeededMin.toString());
                        }
                        show(warning);
                    } else {
                        submitCallback(duration).then(() => {
                            firstDialog.remove();
                        });
                    }
                }
            }
        ],
        {
            modal: true,
            closable: false,
            isNightMode: store.getState().data.nightMode
        }
    );

    void firstDialog.open();
}

export function createSecondDayDialog(store: DriveStore, submitCallback, translator, requirement) {
    let template = _.template(secondDialogHTML);
    let hours = `0${ Math.floor(requirement / 60) }`.slice(-2);
    let minutes = `0${ Math.round(requirement % 60) }`.slice(-2);
    let secondDialog = new Dialog(
        "second-day-dialog",
        template({ requirement: `${ hours }:${ minutes }` }),
        translator,
        title,
        [
            {
                text: "OK",
                id: "apply-daytwo",
                spinner: true,
                callback: () => {
                    submitCallback().then(() => {
                        secondDialog.remove();
                    }).catch(handleApi);
                }
            }
        ],
        {
            modal: true,
            closable: false,
            isNightMode: store.getState().data.nightMode
        }
    );

    void secondDialog.open();
}

export function createBigDayDialog(store: DriveStore, submitCallback, translator, message) {
    let bigDayDialog = new Dialog("apply-big-day", message, translator, "Apply exemption today", [[
        {
            text: "Cancel",
            id: "button-cancel-big-day",
            className: "button_secondary",
            callback: () => {
                bigDayDialog.remove();
            }
        },
        {
            text: "Apply",
            id: "button-apply-big-day",
            callback: () => {
                submitCallback().then(() => {
                    bigDayDialog.remove();
                });
            },
            spinner: true
        }
    ]], {
        closable: false,
        isNightMode: store.getState().data.nightMode
    });

    void bigDayDialog.open();
}

export function createPersonalConveyanceStatusChangeDialog(store: DriveStore, continueCallback, endCallback, translator) {
    let pcStatusChangeDialog = new Dialog(
        "pc-status-dialog",
        "The system has detected that the ignition is off. Please confirm the action on your Personal Conveyance.",
        translator,
        "Personal Conveyance",
        [
            {
                id: "continue-pc",
                text: "Continue personal conveyance",
                callback: () => {
                    continueCallback();
                    pcStatusChangeDialog.remove();
                }
            }, {
                id: "end-pc",
                className: "button_secondary",
                text: "End personal conveyance",
                callback: () => {
                    endCallback();
                    pcStatusChangeDialog.remove();
                }
            }
        ],
        {
            closable: true,
            modal: true,
            isNightMode: store.getState().data.nightMode
        }
    );

    void pcStatusChangeDialog.open();
}

export function createSituationDialog
    (
        store: DriveStore,
        applyCallback: () => Promise,
        description,
        status,
        translator: Translator,
        actionType: TSituationActionType,
        autoCompletionInput?: AutoCompletionInput
    ) {
    let id = `apply-${ status }`;
    let dialogTitle = "Apply exemption today";
    let buttonText = "Apply";
    let buttonId = `button-apply-${ status }`;

    switch (actionType) {
        case "remove":
            id = `remove-${ status }`;
            dialogTitle = "Remove";
            buttonText = "Proceed";
            buttonId = `button-remove-${ status }`;
            break;
        case "applyADC":
            dialogTitle = "Adverse driving conditions";
            break;
        case "applyERR":
            dialogTitle = "Apply Reduced Rest Today";
            break;
        case "applyPC":
            dialogTitle = "Apply Personal Conveyance (PC)?";
            break;
        case "applyExemption":
            dialogTitle = "Apply exemption";
            break;
        case "startEmergencyResponse":
            dialogTitle = "Start emergency response exemption";
            buttonText = "Start";
            buttonId = `button-start-${ status }`;
            break;
        case "endEmergencyResponse":
            id = `end-${ status }`;
            dialogTitle = "End emergency response exemption";
            buttonText = "End";
            buttonId = `button-end-${ status }`;
            break;
        default:
            break;
    }

    let situationDialog = new Dialog(
        id,
        description,
        translator,
        dialogTitle,
        [[
            {
                text: "Cancel",
                id: `button-cancel-${ status }`,
                className: "button_secondary",
                callback: () => {
                    autoCompletionInput && autoCompletionInput.remove();
                    situationDialog.remove();
                }
            },
            {
                text: buttonText,
                id: buttonId,
                callback: () => {
                    applyCallback().then(() => {
                        autoCompletionInput && autoCompletionInput.remove();
                        situationDialog.remove();
                    }, () => {
                        situationDialog.waiting.stop();
                    });
                },
                spinner: true
            }
        ]],
        {
            onCancelCallback: () => {
                autoCompletionInput && autoCompletionInput.remove();
                situationDialog.remove();
            },
            isNightMode: store.getState().data.nightMode
        }
    );

    void situationDialog.open().then(() => {
        const annotationContainer = document.getElementById("exemption-annotation") as HTMLInputElement;
        // Right now, auto completion input is only supported for the start of an exemption
        if (annotationContainer) {
            if (autoCompletionInput) {
                autoCompletionInput.el = annotationContainer;
                autoCompletionInput.render();
            }
        }
    });
}

// MYG-128589
export function createSplitSleeperEnableDialog(store: DriveStore, confirmCallback: () => void, cancelCallback: () => void, translator: Translator) {
    const turnOnSplitSleeperDisplayMessage = `
        <p>If you have an unpaired split sleeper berth period, your screen will show extended available hours based on a completed split sleeper rest</p>
        <p>Note: This extra time only counts once you complete your second rest period. If you don't finish the split, the extra hours will be removed.</p>`;

    let dialog = new Dialog(
        "split-sleeper-enable",
        turnOnSplitSleeperDisplayMessage,
        translator,
        "Turn on split sleeper display?",
        [[
            {
                text: "Cancel",
                id: "button-cancel-split-sleeper-enable",
                className: "button_secondary",
                callback: () => {
                    cancelCallback();
                    dialog.remove();
                }
            },
            {
                text: "Enable split sleeper display",
                id: "button-confirm-split-sleeper-enable",
                callback: () => {
                    confirmCallback();
                    dialog.remove();
                }
            }
        ]],
        {
            closable: true,
            modal: true,
            isNightMode: store.getState().data.nightMode,
            onCancelCallback: () => {
                cancelCallback();
                dialog.remove();
            }
        }
    );

    void dialog.open();
}

// MYG-128589
export function createSplitSleeperDisableDialog(store: DriveStore, confirmCallback: () => void, cancelCallback: () => void, translator: Translator) {
    const turnOffSplitSleeperDisplayMessage = `
        <p>Your screen will show your standard available hours, with no split extension</p>
        <p>Note: You can still reset your workday by resting in your sleeper berth. This only changes what you see on screen.</p>`;

    let dialog = new Dialog(
        "split-sleeper-disable",
        turnOffSplitSleeperDisplayMessage,
        translator,
        "Turn off split sleeper display?",
        [[
            {
                text: "Cancel",
                id: "button-cancel-split-sleeper-disable",
                className: "button_secondary",
                callback: () => {
                    cancelCallback();
                    dialog.remove();
                }
            },
            {
                text: "Confirm",
                id: "button-confirm-split-sleeper-disable",
                callback: () => {
                    confirmCallback();
                    dialog.remove();
                }
            }
        ]],
        {
            closable: true,
            modal: true,
            isNightMode: store.getState().data.nightMode,
            onCancelCallback: () => {
                cancelCallback();
                dialog.remove();
            }
        }
    );

    void dialog.open();
}
